import { useEffect, useRef } from 'react'
import PhotoSwipe from 'photoswipe'
import 'photoswipe/style.css'
import './photoswipe-custom.css'
import { format } from 'date-fns'
import { nb } from 'date-fns/locale'
import type { ItemWithUsername } from '../../lib/types'
import type { SignedUrlVariants } from '../../lib/api'

interface PhotoViewerProps {
  items: ItemWithUsername[]
  startIndex: number
  isOpen: boolean
  onClose: () => void
  /** Pre-resolved signed URLs keyed by item ID — from resolveSignedUrls() in Gallery.tsx */
  signedUrls: Map<string, SignedUrlVariants>
}

export default function PhotoViewer({ items, startIndex, isOpen, onClose, signedUrls }: PhotoViewerProps) {
  const pswpRef = useRef<PhotoSwipe | null>(null)

  useEffect(() => {
    if (!isOpen) return

    // PhotoSwipe requires predefined dimensions. We don't know the real aspect
    // ratio until each image loads (VIEWER_TRANSFORM only constrains width).
    // Use the viewport as a placeholder so the initial layout is reasonable;
    // the loadComplete handler will immediately correct width/height and reflow
    // each slide once the image's naturalWidth/naturalHeight become available.
    const vw = window.innerWidth
    const vh = window.innerHeight
    const dataSource = items.map((item) => {
      const variants = signedUrls.get(item.id)
      const src = variants?.full ?? (item.storagePath ? '' : item.fullUrl)

      if (item.fileType === 'video') {
        const videoSrc = variants?.full ?? item.fullUrl
        return {
          html: `<video src="${videoSrc}" controls playsinline class="pswp-video" style="width:100%;height:100%;object-fit:contain;display:block"></video>`,
        }
      }

      return { src: src ?? '', width: vw, height: vh, alt: '' }
    })

    const pswp = new PhotoSwipe({
      dataSource,
      index: startIndex,
      bgOpacity: 0.9,
      spacing: 0.1,
      allowPanToNext: true,
      zoom: false,
      clickToCloseNonZoomable: true,
      pinchToClose: true,
      closeOnVerticalDrag: true,
      counter: false,
      arrowKeys: true,
      preload: [1, 2],
      initialZoomLevel: 'fit',
      secondaryZoomLevel: 1,
      maxZoomLevel: 1,
    })

    pswp.on('uiRegister', () => {
      pswp.ui?.registerElement({
        name: 'photo-date',
        order: 9,
        isButton: false,
        appendTo: 'wrapper',
        onInit: (el) => {
          el.style.position = 'absolute'
          el.style.bottom = '20px'
          el.style.left = '50%'
          el.style.transform = 'translateX(-50%)'
          el.style.color = 'white'
          el.style.fontSize = '14px'
          el.style.fontWeight = '500'
          el.style.textShadow = '0 1px 3px rgba(0,0,0,0.5)'
          el.style.zIndex = '10'

          const updateDate = () => {
            const currentIndex = pswp.currIndex ?? 0
            const currentItem = items[currentIndex]
            if (currentItem) {
              const titlePart = currentItem.collectionTitle ? `${currentItem.collectionTitle} • ` : ''
              const datePart = currentItem.createdAt
                ? format(new Date(currentItem.createdAt), 'd. MMMM yyyy', { locale: nb })
                : ''
              el.textContent = datePart
                ? `${titlePart}${datePart} • ${currentItem.username}`
                : `${titlePart}${currentItem.username}`
            }
          }

          pswp.on('change', updateDate)
          updateDate()
        },
      })
    })

    // Correct aspect ratio on a slide using the image's natural dimensions.
    //
    // PhotoSwipe uses placeholder dimensions (vw × vh) until the real size is
    // known. We fix this in two places:
    //
    // 1. loadComplete — fires when an image finishes loading while a Slide
    //    already exists for it (first-ever load of the current slide, or the
    //    first slide when it loads after the opening animation).
    //
    // 2. afterSetContent — fires after a Slide is fully appended. For images
    //    that were preloaded by the content loader *before* a Slide was
    //    created (i.e. the user was on a nearby slide), loadComplete already
    //    fired with content.slide === undefined, so it never corrected the
    //    dimensions. When the user navigates to that slide, a new Slide is
    //    created from the already-loaded Content, and we must apply the fix
    //    immediately in afterSetContent.
    //
    // We mutate the live Slide directly (no refreshSlideContent / reload).
    // We also patch the dataSource entry so any future slide recreation uses
    // the correct dimensions.
    type DsEntry = { src: string; width: number; height: number; alt: string }

    const fixSlideAspectRatio = (slide: PhotoSwipe['currSlide'], img: HTMLImageElement, index: number) => {
      if (!slide) return
      const nw = img.naturalWidth
      const nh = img.naturalHeight
      if (!nw || !nh) return

      // Patch dataSource for future recreations.
      const ds = pswp.options.dataSource as Array<DsEntry>
      const entry = ds[index]
      if (entry) {
        entry.width = nw
        entry.height = nh
      }

      // Skip reflow when dimensions are already correct.
      if (slide.width === nw && slide.height === nh) return

      slide.width = nw
      slide.height = nh
      slide.calculateSize()
      slide.zoomAndPanToInitial()
      slide.applyCurrentZoomPan()
      slide.updateContentSize()
    }

    // Case 1: image loads while its slide is active (or nearby and not yet preloaded).
    pswp.on('loadComplete', ({ content, slide }) => {
      if (content.type !== 'image') return
      const img = content.element as HTMLImageElement
      if (!img || !slide) return
      fixSlideAspectRatio(slide, img, content.index)
    })

    // Case 2: slide was just created from an already-loaded preloaded image.
    // afterSetContent fires at the end of Slide.append(); by this point the
    // content element exists and, if preloaded, naturalWidth is already set.
    pswp.on('afterSetContent', ({ slide }) => {
      if (!slide || slide.content.type !== 'image') return
      const img = slide.content.element as HTMLImageElement | undefined
      if (!img || !img.complete || img.naturalWidth === 0) return
      fixSlideAspectRatio(slide, img, slide.index)
    })

    pswp.on('change', () => {
      // Pause any video that is no longer the current slide
      document.querySelectorAll<HTMLVideoElement>('.pswp-video').forEach((video) => {
        if (!video.paused) video.pause()
      })
    })

    pswp.on('close', () => {
      document.querySelectorAll<HTMLVideoElement>('.pswp-video').forEach((video) => {
        if (!video.paused) video.pause()
      })
      onClose()
    })

    pswp.init()
    pswpRef.current = pswp

    return () => {
      pswpRef.current?.destroy()
      pswpRef.current = null
    }
  // startIndex is intentionally excluded: it is only used at construction time.
  // Navigating within PhotoSwipe is managed internally; React re-running this effect
  // on every arrow-key press would destroy and recreate the viewer unnecessarily.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, isOpen, onClose, signedUrls])

  return null
}
